/**
 * König-Fahrzeugimport mit Abgleich-Übersicht (export(1).xlsx).
 * Ablauf: Datei wählen → Vorschau/Abgleich prüfen (schreibt NICHTS) →
 * erst nach visueller Freigabe „Import ausführen".
 * Regeln: B2B=true/B2C=false, Wagennummer=Primärschlüssel, CSV_LR → reserviert
 * für Leasingübernahme (Verkauf gesperrt), bereits verkaufte übersprungen.
 */
import { useRef, useState, useEffect } from "react";
import { Car, Upload, CheckCircle2, AlertTriangle, Loader2, FileSpreadsheet, Lock, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { CollapsibleCard } from "./_shared";
import api from "../../lib/api";
import { toast } from "sonner";

export function KoenigVehicleImportSection({ currentUser }) {
    void currentUser;
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [checking, setChecking] = useState(false);
    const [preview, setPreview] = useState(null);   // { import_id, summary, sample }
    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState(null);
    const [showOnlySystem, setShowOnlySystem] = useState(false);
    const [restoredAt, setRestoredAt] = useState(null);
    const [autoDeactivate, setAutoDeactivate] = useState(true);
    const [deactivating, setDeactivating] = useState(false);
    const [deactivatedCount, setDeactivatedCount] = useState(null);
    const [linking, setLinking] = useState(false);
    const [linkInfo, setLinkInfo] = useState(null);
    const [dedupe, setDedupe] = useState(null);
    const [dedupeBusy, setDedupeBusy] = useState(false);

    const previewDedupe = async () => {
        setDedupeBusy(true);
        try {
            const { data } = await api.get("/dealer/koenig/vehicles/dedupe/preview");
            setDedupe(data);
            if (!data.removable) toast.success("Keine Duplikate gefunden — alles sauber.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dedupe-Prüfung fehlgeschlagen");
        } finally { setDedupeBusy(false); }
    };

    const runDedupe = async () => {
        setDedupeBusy(true);
        try {
            const { data } = await api.post("/dealer/koenig/vehicles/dedupe/run");
            toast.success(`${data.deleted} doppelte Fahrzeuge zusammengeführt${data.protected ? ` · ${data.protected} mit Vorgang geschützt` : ""}.`);
            setDedupe({ duplicate_groups: 0, removable: 0, sample: [] });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Dedupe-Lauf fehlgeschlagen");
        } finally { setDedupeBusy(false); }
    };

    // Beim Öffnen die letzte serverseitige Prüfung/Session wiederherstellen,
    // damit der „Import ausführen"-Button auch nach einem Neuladen verfügbar ist.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/dealer/koenig/vehicles/import/latest");
                const sess = data.session;
                if (!alive || !sess) return;
                if (sess.status === "preview" || sess.status === "running") {
                    setPreview({
                        import_id: sess.import_id, summary: sess.summary,
                        sample: sess.sample, only_in_system_items: sess.only_in_system_items,
                        skipped_examples: sess.skipped_examples,
                    });
                    setRestoredAt(sess.created_at);
                    if (sess.status === "running") { setRunning(true); pollStatus(sess.import_id); }
                } else if (sess.status === "done") {
                    setPreview({
                        import_id: sess.import_id, summary: sess.summary,
                        sample: sess.sample, only_in_system_items: sess.only_in_system_items,
                        skipped_examples: sess.skipped_examples,
                    });
                    if (sess.deactivated_count) setDeactivatedCount(sess.deactivated_count);
                    const { data: st } = await api.get(`/dealer/koenig/vehicles/import/${sess.import_id}/status`);
                    if (alive) setStatus(st);
                }
            } catch { /* keine Session vorhanden */ }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reset = () => {
        setFile(null); setPreview(null); setStatus(null); setRestoredAt(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const onPick = (e) => {
        setFile((e.target.files || [])[0] || null);
        setPreview(null); setStatus(null); setRestoredAt(null);
    };

    const doPreview = async () => {
        if (!file) { toast.error("Bitte zuerst eine Excel-Datei (.xlsx) wählen"); return; }
        setChecking(true); setPreview(null); setStatus(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/dealer/koenig/vehicles/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(data);
            toast.success(`${data.summary.rows_read} Zeilen abgeglichen`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Datei konnte nicht geprüft werden");
        } finally { setChecking(false); }
    };

    const pollStatus = async (importId) => {
        try {
            const { data } = await api.get(`/dealer/koenig/vehicles/import/${importId}/status`);
            setStatus(data);
            if (data.status === "done") {
                setRunning(false);
                toast.success(`Import fertig: ${data.created} neu, ${data.updated} aktualisiert`);
                return;
            }
            if (data.status === "error") {
                setRunning(false);
                toast.error(`Import-Fehler: ${data.error || "unbekannt"}`);
                return;
            }
            setTimeout(() => pollStatus(importId), 1200);
        } catch {
            setTimeout(() => pollStatus(importId), 2000);
        }
    };

    const startImport = async () => {
        if (!preview?.import_id) return;
        setRunning(true);
        setStatus({ status: "running", processed: 0, total: preview.summary.total, created: 0, updated: 0 });
        try {
            await api.post(`/dealer/koenig/vehicles/import/${preview.import_id}/commit?deactivate_sold_off=${autoDeactivate}`);
            pollStatus(preview.import_id);
        } catch (e) {
            setRunning(false);
            toast.error(e?.response?.data?.detail || "Import konnte nicht gestartet werden");
        }
    };

    const deactivateSoldOff = async () => {
        if (!preview?.import_id) return;
        setDeactivating(true);
        try {
            const { data } = await api.post(`/dealer/koenig/vehicles/import/${preview.import_id}/deactivate-sold-off`);
            setDeactivatedCount(data.deactivated);
            toast.success(`${data.deactivated} abverkaufte Fahrzeug(e) archiviert & aus dem Marktplatz genommen`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Deaktivieren fehlgeschlagen");
        } finally { setDeactivating(false); }
    };

    const s = preview?.summary;
    const isSold = s?.mode === "sold";
    const skippedExamples = preview?.skipped_examples || [];
    const [showSkipped, setShowSkipped] = useState(false);
    const soldOffCount = (preview?.only_in_system_items || []).filter((i) => !i.sold).length;
    const done = status?.status === "done";
    const pct = status && status.total ? Math.min(100, Math.round((status.processed / status.total) * 100)) : 0;

    const actionBadge = (a) => {
        if (a === "new") return <span className="text-[9px] uppercase bg-emerald-100 text-emerald-700 px-1 rounded-sm">neu</span>;
        if (a === "update") return <span className="text-[9px] uppercase bg-blue-100 text-blue-700 px-1 rounded-sm">aktualisiert</span>;
        return <span className="text-[9px] uppercase bg-gray-200 text-gray-600 px-1 rounded-sm">übersprungen</span>;
    };

    return (
        <CollapsibleCard
            icon={Car}
            iconClass="text-[#FFCC00]"
            title="Fahrzeug-Import — Datei-Abgleich (Excel & CSV)"
            hint={'Erkennt automatisch: Bestandsliste (export.xlsx) ODER Verkaufsliste (inkl. Käuferdaten). Abgleich org-weit per Wagennummer/FIN — erst prüfen, dann importieren.'}
            testId="koenig-vehicle-import-section"
        >
            {/* Datei wählen */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xlsm"
                    onChange={onPick}
                    className="hidden"
                    data-testid="koenig-import-file-input"
                />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm font-bold border-[#111827]" data-testid="koenig-import-pick-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Datei wählen
                </Button>
                {file && (
                    <span className="text-xs text-gray-700 font-medium truncate max-w-[280px]" data-testid="koenig-import-filename">
                        {file.name}
                    </span>
                )}
                <Button type="button" onClick={doPreview} disabled={!file || checking} className="rounded-sm font-bold bg-[#111827] hover:bg-black text-white" data-testid="koenig-import-preview-btn">
                    {checking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Abgleich prüfen
                </Button>
                {(preview || status) && (
                    <Button type="button" variant="ghost" onClick={reset} className="rounded-sm text-xs text-gray-500" data-testid="koenig-import-reset-btn">
                        Zurücksetzen
                    </Button>
                )}
                {/* Aug-2026 (User-Wunsch): Backfill für ältere Importe — verknüpft alle
                    noch unverknüpften Archiv-Verkäufe mit Kunde + Fahrzeugakte. */}
                <Button type="button" variant="outline" disabled={linking}
                    onClick={async () => {
                        setLinking(true);
                        try {
                            const { data } = await api.post("/dealer/koenig/vehicles/link-archive");
                            setLinkInfo(data);
                            toast.success(data.pending > 0
                                ? `Verknüpfung gestartet: ${data.pending} Archiv-Einträge werden im Hintergrund verknüpft.`
                                : "Alles bereits verknüpft — nichts zu tun.");
                        } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Starten der Verknüpfung"); }
                        setLinking(false);
                    }}
                    className="rounded-sm font-bold border-emerald-600 text-emerald-800 ml-auto"
                    title="Für frühere Importe: verknüpft alle Archiv-Verkäufe nachträglich mit Kunde + Fahrzeugakte (per FIN / Kd-Nr)"
                    data-testid="koenig-link-archive-btn">
                    {linking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Archiv nachverknüpfen
                </Button>
            </div>
            {linkInfo && (
                <p className="text-[11px] text-emerald-800 -mt-2 mb-3" data-testid="koenig-link-archive-info">
                    {linkInfo.pending > 0
                        ? `▶ Läuft im Hintergrund: ${linkInfo.pending} unverknüpfte Archiv-Einträge. Seite kann verlassen werden.`
                        : "✓ Alle Archiv-Verkäufe sind bereits mit Kunden & Fahrzeugakten verknüpft."}
                </p>
            )}

            {/* Alt-Duplikate bereinigen (einmaliger Lauf) */}
            <div className="mb-4 border border-purple-200 bg-purple-50/50 rounded-sm p-3" data-testid="vehicle-dedupe-block">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-xs font-bold text-purple-900">Alt-Duplikate bereinigen</div>
                        <p className="text-[11px] text-purple-800/80">Führt bereits doppelt importierte Fahrzeuge (gleiche Wagennr./FIN) zusammen — behält je Fahrzeug eines.</p>
                    </div>
                    <Button type="button" variant="outline" onClick={previewDedupe} disabled={dedupeBusy}
                        className="rounded-sm text-xs h-8 border-purple-400 text-purple-800 hover:bg-purple-100" data-testid="dedupe-preview-btn">
                        {dedupeBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Duplikate suchen
                    </Button>
                    {dedupe && dedupe.removable > 0 && (
                        <Button type="button" onClick={runDedupe} disabled={dedupeBusy}
                            className="rounded-sm text-xs h-8 bg-purple-700 hover:bg-purple-800 text-white font-bold" data-testid="dedupe-run-btn">
                            {dedupe.removable} Doppel zusammenführen
                        </Button>
                    )}
                </div>
                {dedupe && (
                    <p className="text-[11px] text-purple-900 mt-2" data-testid="dedupe-result">
                        {dedupe.removable > 0
                            ? `${dedupe.duplicate_groups} Fahrzeug(e) mehrfach vorhanden — ${dedupe.removable} überzählige Einträge können zusammengeführt werden.`
                            : "Keine Duplikate gefunden."}
                    </p>
                )}
            </div>

            {/* Abgleich-Übersicht */}
            {s && (
                <div className="border border-gray-200 rounded-sm overflow-hidden mb-4" data-testid="koenig-import-preview">
                    {restoredAt && !status && (
                        <div className="px-4 py-2 text-[11px] bg-blue-50 border-b border-blue-100 text-blue-900 flex items-center gap-1.5" data-testid="koenig-import-restored-note">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Letzte Prüfung vom {new Date(restoredAt).toLocaleString("de-DE")} wiederhergestellt — du kannst den Import unten freigeben oder eine neue Datei prüfen.
                        </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200">
                        <Stat label="Neu" value={s.new} cls="text-emerald-700" testid="kvi-new" />
                        <Stat label="Aktualisiert" value={s.update} cls="text-blue-700" testid="kvi-update" />
                        <Stat label={isSold ? "Bereits erfasst" : "Nur im System"} value={isSold ? s.skipped_sold : s.only_in_system} cls="text-amber-700" testid="kvi-only-system" />
                        <Stat label={isSold ? "Übersprungen (ungültig)" : "Übersprungen (verkauft)"} value={isSold ? s.skipped_invalid : s.skipped_sold} cls="text-gray-600" testid="kvi-skipped-sold" />
                    </div>
                    {isSold && (
                        <div className="px-4 py-2 text-[11px] bg-purple-50 border-t border-purple-100 text-purple-900" data-testid="kvi-sold-mode-note">
                            Format erkannt: <b>Verkaufsliste</b> — Fahrzeuge werden als <b>verkaufte Archiv-Fahrzeuge inkl. Käuferdaten</b> importiert
                            (nicht in der Börse sichtbar, per FIN/Wagennr auffindbar). Bei doppelter FIN zählt der neueste Verkauf.
                            {s.dup_in_file ? <> · {s.dup_in_file} Datei-Duplikat(e) zusammengeführt</> : null}
                            {s.location_matched ? <> · {s.location_matched} Standorte über „VK-Standort" zugeordnet</> : null}
                            {s.customer_vehicle_hits ? <> · <b>{s.customer_vehicle_hits}</b> FIN(s) bereits als Kundenfahrzeug bekannt</> : null}
                        </div>
                    )}
                    {!isSold && (
                    <div className="px-4 py-2 text-[11px] bg-amber-50 border-t border-amber-100 text-amber-900 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="kvi-rules-summary">
                        <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> <b>{s.leasing_reserved}</b> reserviert für Leasingübernahme (CSV_LR, Verkauf gesperrt)</span>
                        <span>Sichtbarkeit: <b>B2B</b> ja · <b>B2C</b> nein</span>
                        {s.skipped_invalid ? <span>{s.skipped_invalid} übersprungen (Marke/Modell fehlt)</span> : null}
                        {s.customer_vehicle_hits ? <span data-testid="kvi-cv-hits"><b>{s.customer_vehicle_hits}</b> FIN(s) bereits als Kundenfahrzeug bekannt</span> : null}
                    </div>
                    )}
                    {skippedExamples.length > 0 && (
                        <div className="border-t border-red-100">
                            <button type="button" onClick={() => setShowSkipped((v) => !v)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left text-[11px] font-bold text-red-800 hover:bg-red-50 transition-colors"
                                data-testid="kvi-skipped-toggle" aria-expanded={showSkipped}>
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showSkipped ? "rotate-180" : ""}`} />
                                Übersprungene Zeilen mit Grund anzeigen ({skippedExamples.length}{s.skipped_invalid > skippedExamples.length ? ` von ${s.skipped_invalid}` : ""})
                            </button>
                            {showSkipped && (
                                <div className="max-h-48 overflow-y-auto border-t border-red-100" data-testid="kvi-skipped-list">
                                    {skippedExamples.map((r, i) => (
                                        <div key={i} className="px-4 py-1 text-[11px] text-gray-600 border-t border-gray-50 flex gap-3">
                                            <span className="font-mono text-gray-400">Zeile {r.row}</span>
                                            <span className="font-medium text-red-700">{r.reason}</span>
                                            <span className="truncate">{r.hint}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="px-4 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-200">
                        {s.rows_read} Zeilen gelesen · Abgleich per <b>{isSold ? "FIN (Fg-Nr)" : "Wagennummer"}</b> · es wurde noch NICHTS geschrieben.
                    </div>

                    {/* Prominenter Freigabe-Balken direkt unter der Übersicht */}
                    {!done && (
                        <div className="px-4 py-3 bg-[#FFFBEB] border-t border-amber-200 flex flex-col gap-3" data-testid="koenig-import-action-bar">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="flex-1 text-xs text-gray-700">
                                    <b>Bereit zur Freigabe.</b> Übersicht geprüft? Dann jetzt importieren — <b>{s.new}</b> neu · <b>{s.update}</b> aktualisiert{isSold ? <> · <b>Verkaufte Fahrzeuge inkl. Käufer</b></> : <> · <b>{s.leasing_reserved}</b> für Leasing reserviert</>}.
                                </div>
                                <Button
                                    type="button"
                                    onClick={startImport}
                                    disabled={running}
                                    className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider disabled:opacity-50 whitespace-nowrap"
                                    data-testid="koenig-import-execute-btn"
                                >
                                    {running ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" /> Importiert…</> : <><Upload className="w-4 h-4 mr-1.5 inline" /> Import ausführen ({s.total})</>}
                                </Button>
                            </div>
                            {soldOffCount > 0 && (
                                <label className="flex items-start gap-2 text-[11px] text-gray-700 cursor-pointer select-none" data-testid="koenig-auto-deactivate-label">
                                    <input
                                        type="checkbox"
                                        checked={autoDeactivate}
                                        onChange={(e) => setAutoDeactivate(e.target.checked)}
                                        disabled={running}
                                        className="mt-0.5 accent-[#111827]"
                                        data-testid="koenig-auto-deactivate-checkbox"
                                    />
                                    <span>Beim Import die <b>{soldOffCount}</b> abverkauften/entfallenen Fahrzeuge (nicht mehr in der Datei) automatisch archivieren & aus dem Marktplatz nehmen. Bereits verkaufte bleiben unangetastet.</span>
                                </label>
                            )}
                        </div>
                    )}

                    {/* Nur-im-System-Bericht (fehlende Wagennummern) */}
                    {s.only_in_system > 0 && (
                        <div className="border-t border-amber-100">
                            <button
                                type="button"
                                onClick={() => setShowOnlySystem((v) => !v)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left text-[11px] font-bold text-amber-800 hover:bg-amber-50 transition-colors"
                                data-testid="koenig-only-system-toggle"
                                aria-expanded={showOnlySystem}
                            >
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOnlySystem ? "rotate-180" : ""}`} />
                                Nur-im-System-Bericht: {s.only_in_system} Fahrzeug(e) fehlen in der neuen Datei (evtl. abverkauft/entfallen)
                            </button>
                            {showOnlySystem && (
                                <div className="border-t border-amber-100" data-testid="koenig-only-system-list">
                                    {soldOffCount > 0 && (
                                        <div className="px-4 py-2 bg-amber-50 flex items-center justify-between gap-2">
                                            <span className="text-[11px] text-amber-900">{deactivatedCount != null ? `${deactivatedCount} Fahrzeug(e) archiviert & aus dem Marktplatz genommen.` : `${soldOffCount} nicht mehr im Bestand — jetzt archivieren?`}</span>
                                            {deactivatedCount == null && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    onClick={deactivateSoldOff}
                                                    disabled={deactivating}
                                                    className="rounded-sm text-[11px] h-7 border-amber-400 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                                                    data-testid="koenig-deactivate-sold-off-btn"
                                                >
                                                    {deactivating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                                                    {soldOffCount} abverkaufte deaktivieren
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                    <div className="max-h-64 overflow-y-auto">
                                    <table className="w-full text-xs">
                                        <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] sticky top-0">
                                            <tr>
                                                <th className="text-left px-3 py-2">Wagennr.</th>
                                                <th className="text-left px-3 py-2">Fahrzeug</th>
                                                <th className="text-left px-3 py-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(preview.only_in_system_items || []).map((r, i) => (
                                                <tr key={i} className="border-t border-gray-100" data-testid={`koenig-only-system-row-${i}`}>
                                                    <td className="px-3 py-1.5 text-gray-500 font-mono">{r.wagennummer}</td>
                                                    <td className="px-3 py-1.5 font-medium text-[#111827]">{[r.brand, r.model].filter(Boolean).join(" ") || "—"}</td>
                                                    <td className="px-3 py-1.5">
                                                        {r.sold
                                                            ? <span className="text-[9px] uppercase bg-red-100 text-red-700 px-1 rounded-sm">verkauft</span>
                                                            : <span className="text-[9px] uppercase bg-gray-200 text-gray-600 px-1 rounded-sm">aktiv</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Stichprobe */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="koenig-import-sample">
                            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                                <tr>
                                    <th className="text-left px-3 py-2">Wagennr.</th>
                                    <th className="text-left px-3 py-2">Fahrzeug</th>
                                    <th className="text-left px-3 py-2">FIN</th>
                                    {isSold && <th className="text-left px-3 py-2">Käufer</th>}
                                    {isSold && <th className="text-left px-3 py-2">Verkauft am</th>}
                                    <th className="text-right px-3 py-2">Preis (B2C)</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.sample.map((r, i) => (
                                    <tr key={i} className="border-t border-gray-100" data-testid={`kvi-row-${i}`}>
                                        <td className="px-3 py-1.5 text-gray-500 font-mono">{r.wagennummer}</td>
                                        <td className="px-3 py-1.5 font-medium text-[#111827]">
                                            {[r.brand, r.model].filter(Boolean).join(" ") || "—"}
                                            {r.is_leasing && (
                                                <span className="ml-1 text-[9px] uppercase bg-purple-100 text-purple-700 px-1 rounded-sm inline-flex items-center gap-0.5" data-testid={`kvi-row-leasing-${i}`}>
                                                    <Lock className="w-2.5 h-2.5" /> Leasing{r.leasing_linked ? " ✓" : ""}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-1.5 text-gray-600 font-mono">{r.vin || "—"}</td>
                                        {isSold && <td className="px-3 py-1.5 text-gray-700">{r.kunde || "—"}</td>}
                                        {isSold && <td className="px-3 py-1.5 text-gray-500">{r.sold_at ? new Date(r.sold_at).toLocaleDateString("de-DE") : "—"}</td>}
                                        <td className="px-3 py-1.5 text-right text-gray-700">{r.price != null ? `${Number(r.price).toLocaleString("de-DE")} €` : "—"}</td>
                                        <td className="px-3 py-1.5">{actionBadge(r.action)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Fortschritt / Ergebnis */}
            {status && (
                <div className="border border-gray-200 rounded-sm p-4 mb-4" data-testid="koenig-import-progress">
                    {!done ? (
                        <>
                            <div className="flex items-center justify-between text-xs mb-2">
                                <span className="font-bold flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin text-[#FFCC00]" /> Import läuft…</span>
                                <span className="text-gray-500">{status.processed} / {status.total}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#111827] transition-all" style={{ width: `${pct}%` }} />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-start gap-2 text-sm" data-testid="koenig-import-done">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold text-[#111827]">Import abgeschlossen</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    {status.created} neue Fahrzeuge · {status.updated} aktualisiert · {status.skipped} übersprungen.
                                </p>
                                {(status.deactivated_count || deactivatedCount) ? (
                                    <p className="text-xs text-amber-700 mt-0.5" data-testid="koenig-import-deactivated-count">
                                        {status.deactivated_count || deactivatedCount} abverkaufte Fahrzeuge archiviert & aus dem Marktplatz genommen.
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Import ausführen — Aktionsbalken befindet sich oben in der Übersicht */}

            <div className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Regeln: Wagennummer = Primärschlüssel (keine interne Angebotsnummer vor Verkauf). CSV_LR-Fahrzeuge werden als „reserviert für Leasingübernahme" markiert und für den Verkauf gesperrt (VIN-Verknüpfung zur Leasingrückgabe). Bereits im System verkaufte Fahrzeuge werden übersprungen.
            </div>
        </CollapsibleCard>
    );
}

function Stat({ label, value, cls = "text-[#111827]", testid }) {
    return (
        <div className="bg-white px-4 py-3" data-testid={testid}>
            <div className={`text-2xl font-black ${cls}`}>{value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
        </div>
    );
}
